import { useMemo } from 'react';

import { useRentalManagerUserPropertySummaryWebApiHandler_applications } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { useRentalManagerUserPropertySummaryWebApiHandler_listing } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../actions/PropertiesActions';

import TimeConstants from '../../../constants/TimeConstants';
import ApplicationSummary from '../../../model/ApplicationSummary';
import Syndication from '../../../model/Syndication';

import type { Dispatch } from 'react';
import type { QueryClient } from '@zg-rentals/react-query5';
import type { UserPropertySummaryListingResponseWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ApplicationSummaryProps } from '../../../model/ApplicationSummary';
import type { CurrentPropertyProps } from '../../../model/CurrentProperty';

/**
 * when using this query we need to sync data to the redux store in high level components
 * child components or components that only consume data do not need to sync data
 *
 * high level components:
 *   ConnectedListing
 *
 * implement by calling syncCurrentProperty on data change
 *
 *   useEffect(() => {
 *     if (listingSummaryData) {
 *       syncCurrentProperty({ data: listingSummaryData, dispatch });
 *     }
 *   }, [listingSummaryData]);
 */
const useGetListingSummary = (listingAlias: string, queryOptions = {}) => {
  return useRentalManagerUserPropertySummaryWebApiHandler_listing(
    { listingAlias },
    {
      ...queryOptions,
      enabled: Boolean(listingAlias),
      staleTime: TimeConstants.SECONDS_10,
    },
  );
};

const syncCurrentProperty = ({
  data,
  dispatch,
}: {
  data: UserPropertySummaryListingResponseWebDto;
  dispatch: Dispatch<{ type: string; payload: Partial<CurrentPropertyProps> }>;
}) => {
  const dataToUpdate = {
    active: data.isActive,
    isFeed: data.isFeed,
    hasAnalyticsRentComps: data.hasAnalyticsRentComps,
    expirationDateTimestamp: data.expirationMs ? String(data.expirationMs) : '',
    // @ts-expect-error
    syndication: Syndication(data.syndication),
    dateLastActivatedTimestamp: data.lastActivatedMs ? String(data.lastActivatedMs) : '',
    contactName: data.contactName,
    canBeRelisted: data.canBeRelisted,
  };

  // @ts-expect-error
  dispatch(PropertiesActions.updateCurrentProperty(dataToUpdate));
};

/**
 * there are cases where the api response returns a 200 status, but
 * we still want to display an Alert.
 *
 * check for this within the component and display a client error message
 *
 * if we trigger a Promise.reject or throw Error the react-query state will not be correct
 * need to investigate for documentation around this
 *
 * ex: restrictions
 */
const useGetApplicationsSummary = (listingAlias: string, getActive = false, queryOptions = {}) => {
  const result = useRentalManagerUserPropertySummaryWebApiHandler_applications(
    { listingAlias, getActive },
    {
      ...queryOptions,
      enabled: Boolean(listingAlias),
      staleTime: TimeConstants.SECONDS_10,
    },
  );

  const data = useMemo(() => (result.data ? ApplicationSummary(result.data) : undefined), [result.data]);

  return { ...result, data };
};

const setQueryApplicationsSummary = ({
  config,
  payload,
}: {
  config: { queryClient: QueryClient; listingAlias: string; getActive: boolean };
  payload: Partial<ApplicationSummaryProps>;
}) => {
  const { queryClient, listingAlias, getActive } = config;

  queryClient.setQueryData(
    useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias, getActive }),
    (oldData: Record<string, unknown>) => {
      // Only update if we have existing data (don't clear error states)
      if (!oldData) {
        return oldData;
      }

      return {
        ...ApplicationSummary(oldData),
        ...payload,
      };
    },
  );
};

export { useGetApplicationsSummary, useGetListingSummary, setQueryApplicationsSummary, syncCurrentProperty };
